/* ============================================================================
 * Product context menu  +  Wishlist Hero proxy hearts
 * ----------------------------------------------------------------------------
 * This file handles two things (they are coupled because the wishlist button
 * is moved into the context menu):
 *
 *   1. The product context menu on right-click (desktop) / long-press (mobile)
 *   2. Custom "heart" buttons that proxy the real (hidden) Wishlist Hero SDK
 *      button — SDK init, state sync and repair of SDK glitches
 *
 * NOTE: The retry counts / timeouts in CONFIG below are intentionally
 * aggressive because WishListHero_SDK is unreliable (it sometimes silently
 * drops a button and sometimes creates a stray one). Do not "optimize" them,
 * or the race conditions will come back.
 *
 * DEBUG: Setting WLH_DEBUG = true prints the full [WLH-DEBUG] trace to the console.
 * ========================================================================== */
document.addEventListener('DOMContentLoaded', function () {
  // --------------------------------------------------------------------------
  // Config — all tuning knobs in one place (values unchanged)
  // --------------------------------------------------------------------------
  var CONFIG = {
    LONG_PRESS_MS: 550,           // mobile long-press → context menu
    MENU_EDGE_PADDING: 8,         // keep the menu this far from the viewport edge
    INIT_POLL_MS: 300,            // polling interval while waiting for the SDK to be ready
    INIT_POLL_MAX: 60,            // stop polling after this many attempts (~18s)
    // NOTE: the 3 values below (INNER_BTN_WAIT, TILE_TOGGLE, STRAY_REPAIR_STALE)
    // used to be much less patient. On a cold guest/incognito session (no cache,
    // so the WLH SDK bundle is slow to download and boot) 2-3+ seconds are easily
    // needed, and the old values gave up before that (the wishlist option in the
    // context menu stayed empty and a tile-heart click did nothing). On reload the
    // SDK is ready sooner thanks to the warm cache, which is why it looked like it
    // "sometimes works, sometimes doesn't". They are more patient now.
    INNER_BTN_WAIT_RETRIES: 16,   // menu click: wait for the WLH button to render (~4.8s)
    INNER_BTN_WAIT_MS: 300,
    MENU_TOGGLE_RETRIES: 6,       // click again if the toggle isn't confirmed from the menu
    MENU_TOGGLE_CONFIRM_MS: 400,
    TILE_TOGGLE_RETRIES: 20,      // click again if the toggle isn't confirmed from the tile heart (~5s)
    TILE_TOGGLE_WAIT_MS: 250,
    TILE_TOGGLE_CONFIRM_MS: 400,
    STRAY_REPAIR_STALE_MS: 4000,  // still empty this long after init → the SDK dropped the button
    SYNC_DELAYS_MS: [500, 1500, 3000, 6000], // re-sync passes after page load
    OBSERVER_DEBOUNCE_MS: 300,
    REVISIT_SYNC_MS: 500,         // after the tab regains focus / window load
    // The header badge count (GetWishListItemsCount()) sometimes returns a STALE
    // value on a single immediate check — e.g. the heart fill/add is already
    // confirmed but the count has not yet updated inside the SDK. Instead of one
    // check we do several staggered re-checks so the SDK has enough time to
    // settle the count — exactly the same pattern as SYNC_DELAYS_MS.
    BADGE_RETRY_DELAYS_MS: [400, 1000, 2000, 4000]
  };

  var WLH_DEBUG = false;
  function wlhLog() {
    if (WLH_DEBUG && window.console) console.log.apply(console, arguments);
  }
  function wlhWarn() {
    if (WLH_DEBUG && window.console) console.warn.apply(console, arguments);
  }

  // --------------------------------------------------------------------------
  // Module state
  // --------------------------------------------------------------------------
  var originalParent = null;
  var originalNextSibling = null;
  var longPressTimer = null;
  var longPressHandled = false;
  var wlhInteractionInProgress = false;

  // --------------------------------------------------------------------------
  // DOM getters
  // --------------------------------------------------------------------------
  function getMenu() { return document.getElementById('product-context-menu'); }
  function getMenuMobile() { return document.getElementById('product-context-menu-mobile'); }
  function getOpenLink() { return document.getElementById('product-context-menu-open'); }
  function getOpenLinkMobile() { return document.getElementById('product-context-menu-mobile-open'); }

  // --------------------------------------------------------------------------
  // Wishlist state detection
  // Use the rendered state of the hidden WLH button to find out whether the
  // product is in the wishlist (the SDK has no clean API for this, so we have to sniff the DOM).
  // --------------------------------------------------------------------------
  function getWlhFilledState(hiddenBtn) {
    var innerBtn = hiddenBtn && hiddenBtn.querySelector('button');
    if (!innerBtn) return null;

    var label = (innerBtn.getAttribute('aria-label') || '').toLowerCase();
    var title = (innerBtn.getAttribute('title') || '').toLowerCase();
    var className = (innerBtn.className || '').toLowerCase();
    var svg = innerBtn.querySelector('svg');

    if (label.includes('remove') || title.includes('remove')) return true;
    if (className.includes('wlh-in-wishlist') || className.includes('active') || className.includes('wishlisted')) return true;
    if (innerBtn.querySelector('.fa-solid') || innerBtn.querySelector('[data-prefix="fas"]')) return true;
    if (svg) {
      var fillAttr = (svg.getAttribute('fill') || '').toLowerCase();
      if (fillAttr && fillAttr !== 'none' && fillAttr !== 'transparent') return true;
    }
    return false;
  }

  // Make all visible hearts of one product match the state of its hidden button.
  function syncTileHeart(hiddenBtn) {
    if (!hiddenBtn) return;

    var productIdMatch = hiddenBtn.className.match(/wlh-btn-(\d+)/);
    if (!productIdMatch) return;
    var productId = productIdMatch[1];

    var allHearts = document.querySelectorAll('.wlh-tile-heart[data-product-id="' + productId + '"]');
    if (!allHearts.length) return;
    if (!hiddenBtn.querySelector('button')) return;

    var isFilled = getWlhFilledState(hiddenBtn);
    allHearts.forEach(function (visibleHeart) {
      var wasFilled = visibleHeart.classList.contains('wlh-in-wishlist');
      visibleHeart.classList.toggle('wlh-in-wishlist', isFilled);
      if (wasFilled !== isFilled) {
        dispatchHeartStateChanged(visibleHeart, productId, isFilled);
      }
    });
    hiddenBtn.classList.toggle('in-wishlist', isFilled);
  }

  // A new, purely additive event — there was no listener before, so it does not
  // affect any existing functionality. The wishlist page
  // (assets/wishlist-custom.js) listens to it to run the remove animation.
  //
  // "userInitiated" is VERY IMPORTANT: when the WLH SDK first renders a button it
  // shows the "Add to wishlist" state for a few milliseconds (even if the product
  // is already in the wishlist) — in that transient window a passive sync used to
  // mistake it for "removed". With this flag a consumer can tell whether the change
  // came from a user click or just from a background sync pass.
  function dispatchHeartStateChanged(visibleHeart, productId, isFilled) {
    visibleHeart.dispatchEvent(new CustomEvent('wlh:heart-state-changed', {
      bubbles: true,
      detail: {
        productId: productId,
        isFilled: isFilled,
        userInitiated: wlhInteractionInProgress === true
      }
    }));
  }

  // Look at all hidden buttons on the page and sync the hearts of every product.
  // (In carousels one product appears in several tiles — keep them all consistent.)
  function syncAllHearts() {
    var productStates = {};

    document.querySelectorAll('[class*="wlh-btn-"]').forEach(function (hiddenBtn) {
      var productIdMatch = hiddenBtn.className.match(/wlh-btn-(\d+)/);
      if (!productIdMatch) return;
      var productId = productIdMatch[1];

      if (!hiddenBtn.querySelector('button')) return;

      var isFilled = getWlhFilledState(hiddenBtn);
      hiddenBtn.classList.toggle('in-wishlist', isFilled);

      if (isFilled || !(productId in productStates)) {
        productStates[productId] = productStates[productId] || isFilled;
      }
    });

    Object.keys(productStates).forEach(function (productId) {
      var isFilled = productStates[productId];
      document.querySelectorAll('.wlh-tile-heart[data-product-id="' + productId + '"]').forEach(function (visibleHeart) {
        var wasFilled = visibleHeart.classList.contains('wlh-in-wishlist');
        visibleHeart.classList.toggle('wlh-in-wishlist', isFilled);
        if (wasFilled !== isFilled) {
          dispatchHeartStateChanged(visibleHeart, productId, isFilled);
        }
      });
    });
  }

  // --------------------------------------------------------------------------
  // Context menu — open / close / button move
  // --------------------------------------------------------------------------

  // Put the real WLH button, which was moved into the menu, back in its original place.
  function restoreRealBtn() {
    if (!originalParent) return;
    var menu = getMenu();
    var menuMobile = getMenuMobile();
    var realBtn = (menu && menu.querySelector('[class*="wlh-btn-"]')) || (menuMobile && menuMobile.querySelector('[class*="wlh-btn-"]'));
    if (realBtn) {
      realBtn.setAttribute('style', 'display:none;');
      realBtn.setAttribute('aria-hidden', 'true');
      realBtn.setAttribute('tabindex', '-1');
      realBtn.classList.remove('wlh-in-menu', 'product-context-menu__item', 'in-wishlist');

      if (originalNextSibling) {
        originalParent.insertBefore(realBtn, originalNextSibling);
      } else {
        originalParent.appendChild(realBtn);
      }

      setTimeout(function () {
        syncTileHeart(realBtn);
      }, 100);
    }
    originalParent = null;
    originalNextSibling = null;
  }

  function hideMenu() {
    restoreRealBtn();
    var menu = getMenu();
    var menuMobile = getMenuMobile();
    if (menu) menu.hidden = true;
    if (menuMobile) menuMobile.hidden = true;
  }

  // Every showMenu() call gets its own "generation" number — if the user quickly
  // right-clicks somewhere else (or on the same product again), the older pending
  // wait loop recognizes itself as stale and stops quietly, so it never moves the
  // button of the WRONG product into the menu later.
  var menuShowGeneration = 0;

  function showMenu(targetMenu, targetOpenLink, trigger, x, y) {
    menuShowGeneration++;
    var myGeneration = menuShowGeneration;

    repairStrayWlhButtons();
    restoreRealBtn();

    var productUrl = trigger.getAttribute('data-product-url');
    if (productUrl && targetOpenLink) targetOpenLink.setAttribute('href', productUrl);

    var productId = trigger.getAttribute('data-product-id');
    var realBtn = trigger.querySelector('.wlh-btn-' + productId) || document.querySelector('.wlh-btn-' + productId);

    wlhLog('[WLH-DEBUG] showMenu: popup opening', {
      productId: productId,
      time: performance.now().toFixed(0) + 'ms',
      realBtnFound: !!realBtn,
      realBtnHasInnerButton: !!(realBtn && realBtn.querySelector('button')),
      realBtnClasses: realBtn ? realBtn.className : null,
      realBtnHTML: realBtn ? realBtn.outerHTML : null
    });

    if (realBtn) {
      if (realBtn.querySelector('button')) {
        // The SDK has already rendered it — move it into the menu right away
        // (as it used to work before).
        moveRealBtnIntoMenu(realBtn, targetMenu);
      } else {
        // ################ FIX (v2) ################
        // Previously we moved the empty button into the menu IMMEDIATELY and then
        // waited for the SDK to render. But the SDK's rendering process seems to get
        // confused by this very REPARENTING — the button is removed from its
        // ORIGINAL place before the SDK can put anything into it, and then it never
        // renders at all (no matter how long you wait) — which is why the earlier
        // "just wait longer" fix did not help.
        //
        // Now we move the button ONLY once the SDK has rendered it (the real
        // <button> is inside) — until then it stays in its ORIGINAL place (not
        // touched at all) so the SDK's rendering process is not disturbed. As soon
        // as it renders it "pops in" to the menu immediately.
        waitThenMoveIntoMenu(realBtn, targetMenu, CONFIG.INNER_BTN_WAIT_RETRIES, myGeneration);
      }
    }

    targetMenu.hidden = false;

    if (typeof x === 'number' && typeof y === 'number') {
      var menuRect = targetMenu.getBoundingClientRect();
      var maxX = window.innerWidth - menuRect.width - CONFIG.MENU_EDGE_PADDING;
      var maxY = window.innerHeight - menuRect.height - CONFIG.MENU_EDGE_PADDING;
      targetMenu.style.left = Math.min(x, maxX) + 'px';
      targetMenu.style.top = Math.min(y, maxY) + 'px';
    }
  }

  function moveRealBtnIntoMenu(realBtn, targetMenu) {
    originalParent = realBtn.parentNode;
    originalNextSibling = realBtn.nextSibling;

    realBtn.removeAttribute('style');
    realBtn.removeAttribute('aria-hidden');
    realBtn.removeAttribute('tabindex');
    realBtn.classList.add('product-context-menu__item', 'wlh-in-menu');

    targetMenu.appendChild(realBtn);
    syncTileHeart(realBtn);
  }

  // Leave realBtn in its ORIGINAL place (untouched) and wait until the SDK has
  // rendered the real <button> inside — only then move it into the menu.
  // `generation` checks that no NEW showMenu() call has made this older wait
  // obsolete (e.g. the user right-clicked somewhere else in the meantime).
  function waitThenMoveIntoMenu(realBtn, targetMenu, retriesLeft, generation) {
    if (retriesLeft <= 0) return;
    setTimeout(function () {
      if (generation !== menuShowGeneration) return; // a newer showMenu() has run — this one is irrelevant now
      if (targetMenu.hidden) return; // the menu was closed in the meantime

      if (realBtn.querySelector('button')) {
        moveRealBtnIntoMenu(realBtn, targetMenu);
        return;
      }
      waitThenMoveIntoMenu(realBtn, targetMenu, retriesLeft - 1, generation);
    }, CONFIG.INNER_BTN_WAIT_MS);
  }

  // --------------------------------------------------------------------------
  // Context menu — triggers (right-click / long-press / outside-click)
  // --------------------------------------------------------------------------
  document.addEventListener('contextmenu', function (e) {
    if (longPressHandled) { longPressHandled = false; return; }
    var trigger = e.target.closest('.product-context-trigger');
    if (!trigger) { hideMenu(); return; }
    var menu = getMenu();
    if (!menu) return;
    if (window.innerWidth < 768) return;
    e.preventDefault();
    showMenu(menu, getOpenLink(), trigger, e.clientX, e.clientY);
  });

  document.addEventListener('touchstart', function (e) {
    var trigger = e.target.closest('.product-context-trigger');
    var menuMobile = getMenuMobile();
    if (!trigger || !menuMobile) return;
    longPressHandled = false;
    longPressTimer = setTimeout(function () {
      longPressHandled = true;
      showMenu(getMenuMobile(), getOpenLinkMobile(), trigger);
    }, CONFIG.LONG_PRESS_MS);
  }, { passive: true });

  document.addEventListener('touchmove', function () {
    clearTimeout(longPressTimer);
  }, { passive: true });

  document.addEventListener('touchend', function () {
    clearTimeout(longPressTimer);
  });

  document.addEventListener('click', function (e) {
    if (!e.isTrusted) return;

    var menu = getMenu();
    var menuMobile = getMenuMobile();
    var clickedInMenu = (menu && menu.contains(e.target)) || (menuMobile && menuMobile.contains(e.target));

    if (!clickedInMenu) {
      hideMenu();
      return;
    }

    if (!e.target.closest('.wlh-in-menu')) return;

    wlhInteractionInProgress = true;
    wlhLog('[WLH-DEBUG] popup click: user clicked wlh-in-menu button at', performance.now().toFixed(0) + 'ms');

    var waitForInnerBtnAndToggle = function (waitRetriesLeft) {
      var currentBtn = (menu && menu.querySelector('[class*="wlh-btn-"]')) || (menuMobile && menuMobile.querySelector('[class*="wlh-btn-"]'));
      var innerBtn = currentBtn && currentBtn.querySelector('button');

      wlhLog('[WLH-DEBUG] waitForInnerBtnAndToggle', {
        waitRetriesLeft: waitRetriesLeft,
        time: performance.now().toFixed(0) + 'ms',
        currentBtnFound: !!currentBtn,
        innerBtnFound: !!innerBtn,
        currentBtnHTML: currentBtn ? currentBtn.outerHTML : null
      });

      if (!innerBtn) {
        if (waitRetriesLeft > 0) {
          setTimeout(function () { waitForInnerBtnAndToggle(waitRetriesLeft - 1); }, CONFIG.INNER_BTN_WAIT_MS);
        } else {
          wlhWarn('[WLH-DEBUG] waitForInnerBtnAndToggle: GAVE UP — innerBtn kabhi nahi mila, popup band kar rahe hain bina click ke');
          hideMenu();
          wlhInteractionInProgress = false;
        }
        return;
      }

      var stateBeforeClick = getWlhFilledState(currentBtn);
      wlhLog('[WLH-DEBUG] waitForInnerBtnAndToggle: innerBtn mil gaya, click kar rahe hain', {
        stateBeforeClick: stateBeforeClick,
        innerBtnHTML: innerBtn.outerHTML
      });
      innerBtn.click();

      confirmToggle(currentBtn, stateBeforeClick, CONFIG.MENU_TOGGLE_RETRIES);
    };

    var confirmToggle = function (btnRef, stateBeforeClick, retriesLeft) {
      setTimeout(function () {
        var currentBtn = (menu && menu.querySelector('[class*="wlh-btn-"]')) || (menuMobile && menuMobile.querySelector('[class*="wlh-btn-"]'));
        var stateNow = currentBtn ? getWlhFilledState(currentBtn) : null;

        wlhLog('[WLH-DEBUG] confirmToggle check', {
          retriesLeft: retriesLeft,
          time: performance.now().toFixed(0) + 'ms',
          stateBeforeClick: stateBeforeClick,
          stateNow: stateNow,
          changed: stateNow !== stateBeforeClick,
          currentBtnHTML: currentBtn ? currentBtn.outerHTML : null
        });

        if (currentBtn && stateNow === stateBeforeClick && retriesLeft > 0) {
          var innerBtn = currentBtn.querySelector('button');
          wlhLog('[WLH-DEBUG] confirmToggle: state nahi badli, dobara click kar rahe hain', { innerBtnFound: !!innerBtn });
          if (innerBtn) innerBtn.click();
          confirmToggle(currentBtn, stateBeforeClick, retriesLeft - 1);
          return;
        }

        if (stateNow === stateBeforeClick) {
          wlhWarn('[WLH-DEBUG] confirmToggle: GAVE UP — retries khatam, state kabhi confirm nahi hui. FINAL STATE:', stateNow);
        } else {
          wlhLog('[WLH-DEBUG] confirmToggle: SUCCESS — state confirm ho gayi:', stateNow);
        }

        if (currentBtn) syncTileHeart(currentBtn);
        hideMenu();
        wlhInteractionInProgress = false;
      }, CONFIG.MENU_TOGGLE_CONFIRM_MS);
    };

    waitForInnerBtnAndToggle(CONFIG.INNER_BTN_WAIT_RETRIES);
  });

  document.addEventListener('scroll', hideMenu, true);
  window.addEventListener('resize', hideMenu);

  // --------------------------------------------------------------------------
  // Wishlist Hero SDK — button initialization
  // --------------------------------------------------------------------------
  function initSingleWishlistButton(trigger, hiddenBtn, productId) {
    hiddenBtn.innerHTML = ''; // clear the old (possibly broken) content
    hiddenBtn.dataset.wlhInitTime = Date.now(); // track when the init call happened

    window._wlhInstanceCounter = (window._wlhInstanceCounter || 0) + 1;
    var instanceClass = 'wlh-instance-' + window._wlhInstanceCounter;
    hiddenBtn.className = hiddenBtn.className
      .split(' ')
      .filter(function (c) { return c.indexOf('wlh-instance-') !== 0; })
      .join(' ');
    hiddenBtn.classList.add(instanceClass);

    window.WishListHero_SDK.InitializeAddToWishListButton({
      ProductId: Number(productId),
      ProductVariantId: Number(trigger.getAttribute('data-variant-id')),
      ProductTitle: trigger.getAttribute('data-product-title'),
      ProductPrice: Number(trigger.getAttribute('data-product-price')),
      ProductLink: trigger.getAttribute('data-product-link'),
      ProductImage: trigger.getAttribute('data-product-image'),
      ButtonClassElement: instanceClass,
      ButtonMode: 'default',
      WishlistHash: ''
    });
    hiddenBtn.dataset.wlhSdkInit = '1';
  }

  function initWishlistButtons() {
    if (!window.WishListHero_SDK) return false;
    var initialized = false;

    document.querySelectorAll('.product-context-trigger, .title-row, .pdp-mobile-actions').forEach(function (trigger) {
      if (trigger.dataset.wlhInit === '1') return;
      var productId = trigger.getAttribute('data-product-id');
      if (!productId) return;
      var uniqueClass = 'wlh-btn-' + productId;
      var hiddenBtn = trigger.querySelector('.' + uniqueClass) || document.querySelector('.' + uniqueClass);
      if (!hiddenBtn || hiddenBtn.dataset.wlhSdkInit === '1') { trigger.dataset.wlhInit = '1'; return; }

      initSingleWishlistButton(trigger, hiddenBtn, productId);
      trigger.dataset.wlhInit = '1';
      initialized = true;
    });
    return initialized;
  }

  var wlhRetries = 0;
  var wlhInterval = setInterval(function () {
    wlhRetries++;
    initWishlistButtons();
    if (wlhRetries > CONFIG.INIT_POLL_MAX) clearInterval(wlhInterval);
  }, CONFIG.INIT_POLL_MS);

  // --------------------------------------------------------------------------
  // Visible tile heart → proxy click on hidden WLH button
  // --------------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    var heart = e.target.closest('.wlh-tile-heart');
    if (!heart) return;

    e.preventDefault();
    e.stopPropagation();

    var productId = heart.getAttribute('data-product-id');

    // The PDP heart icons (inside .pdp-actions / .pdp-mobile-actions) are not
    // inside .product-context-trigger — they live in separate standalone
    // containers (.title-row, .pdp-mobile-actions) where the hidden button is
    // rendered by initWishlistButtons() itself. That is why both of these
    // containers are included in the tile lookup too.
    var tile = heart.closest('.product-context-trigger, .title-row, .pdp-mobile-actions');

    // Inside .pdp-mobile-actions the Liquid template has no hidden wishlist
    // button of its own (only .title-row does) — that is why there is a
    // document-wide fallback here. It is only safe for this PDP container
    // because a PDP has just ONE product (no carousel cloning, so there is no
    // risk of picking the wrong product).
    var hiddenBtn = tile ? tile.querySelector('.wlh-btn-' + productId) : null;
    if (!hiddenBtn && tile && tile.classList.contains('pdp-mobile-actions')) {
      hiddenBtn = document.querySelector('.wlh-btn-' + productId);
    }

    if (!hiddenBtn) return;

    wlhInteractionInProgress = true;

    var toggleWithRetry = function (retriesLeft) {
      var currentHiddenBtn = tile ? tile.querySelector('.wlh-btn-' + productId) : null;
      if (!currentHiddenBtn && tile && tile.classList.contains('pdp-mobile-actions')) {
        currentHiddenBtn = document.querySelector('.wlh-btn-' + productId);
      }
      var currentInnerBtn = currentHiddenBtn && currentHiddenBtn.querySelector('button');

      if (!currentInnerBtn) {
        if (retriesLeft > 0) {
          setTimeout(function () { toggleWithRetry(retriesLeft - 1); }, CONFIG.TILE_TOGGLE_WAIT_MS);
        } else {
          wlhInteractionInProgress = false;
        }
        return;
      }

      var stateBeforeClick = getWlhFilledState(currentHiddenBtn);
      currentInnerBtn.click();

      setTimeout(function () {
        var afterBtn = tile ? tile.querySelector('.wlh-btn-' + productId) : null;
        var stateAfter = afterBtn ? getWlhFilledState(afterBtn) : null;

        if (afterBtn && stateAfter === stateBeforeClick && retriesLeft > 0) {
          toggleWithRetry(retriesLeft - 1);
          return;
        }

        if (afterBtn) syncTileHeart(afterBtn);
        wlhInteractionInProgress = false;
      }, CONFIG.TILE_TOGGLE_CONFIRM_MS);
    };

    toggleWithRetry(CONFIG.TILE_TOGGLE_RETRIES);
  });

  // --------------------------------------------------------------------------
  // Wishlist Hero SDK — glitch repair
  // The SDK sometimes silently drops a button or creates a stray sibling.
  // Detect such buttons and have the SDK initialize them again from scratch.
  // --------------------------------------------------------------------------
  function repairStrayWlhButtons() {
    if (wlhInteractionInProgress) {
      wlhLog('[WLH-DEBUG] repairStrayWlhButtons: SKIPPED — interaction in progress at', performance.now().toFixed(0) + 'ms');
      return;
    }
    if (!window.WishListHero_SDK) return;

    document.querySelectorAll('.wishlisthero-hidden-btn[class*="wlh-btn-"]').forEach(function (hiddenBtn) {
      var innerDiv = hiddenBtn.querySelector(':scope > div');
      var hasNestedButton = innerDiv && innerDiv.querySelector('button');
      if (hasNestedButton) return;

      var strayBtn = hiddenBtn.nextElementSibling;
      var hasStraySibling =
        strayBtn &&
        strayBtn.tagName === 'BUTTON' &&
        (strayBtn.getAttribute('aria-label') || '').toLowerCase().indexOf('wishlist') !== -1;

      // If hiddenBtn is completely empty (neither rendered properly nor left a
      // stray sibling) and the init call was a long time ago → the SDK silently
      // missed/dropped this call, so initialize it again.
      var initTime = Number(hiddenBtn.dataset.wlhInitTime || 0);
      var initedTooLongAgo = initTime && (Date.now() - initTime > CONFIG.STRAY_REPAIR_STALE_MS);
      var isCompletelyEmpty = !hasStraySibling && !innerDiv && initedTooLongAgo;

      var isBroken = hasStraySibling || isCompletelyEmpty;
      if (!isBroken) return;

      var productIdMatch = hiddenBtn.className.match(/wlh-btn-(\d+)/);
      if (!productIdMatch) return;
      var productId = productIdMatch[1];
      var trigger = hiddenBtn.closest('.product-context-trigger, .title-row, .pdp-mobile-actions') ||
        document.querySelector('.product-context-trigger[data-product-id="' + productId + '"]');
      if (!trigger) return;

      wlhLog('[WLH-DEBUG] repairStrayWlhButtons: RE-INITIALIZING (SDK se dobara) broken button', {
        hiddenBtnClass: hiddenBtn.className,
        productId: productId,
        reason: hasStraySibling ? 'stray-sibling' : 'never-rendered-completely-empty',
        time: performance.now().toFixed(0) + 'ms'
      });

      // ################ BUG FIX (confirmed via wishlist-card DOM dump) ################
      // This used to be just "if (strayBtn) strayBtn.remove();" — meaning whenever
      // isBroken became true because of "isCompletelyEmpty" (SDK slower than 2s —
      // common on a guest/incognito cold load), it removed the NEXT SIBLING ELEMENT
      // without checking whether it really was a stray WLH button.
      // In collection tiles the hidden button is always the LAST element (it has no
      // sibling at all), so this was never noticed — but in the wishlist page cards
      // .wishlist-card__info (title + Add to Cart) comes right after the hidden
      // button, and in slow-SDK guest sessions it was being deleted from the DOM
      // completely (not just hidden, permanently gone).
      // Now we remove it ONLY when hasStraySibling has confirmed that it really is
      // a stray WLH button (<button> + wishlist-related aria-label) — an arbitrary
      // sibling will never be removed just because "the SDK is still slow".
      if (hasStraySibling) strayBtn.remove();
      initSingleWishlistButton(trigger, hiddenBtn, productId);
      setTimeout(function () { syncTileHeart(hiddenBtn); }, 300);
    });
  }

  // --------------------------------------------------------------------------
  // Re-sync scheduling — several passes after load, on DOM changes, on tab focus
  // --------------------------------------------------------------------------
  CONFIG.SYNC_DELAYS_MS.forEach(function (delay) {
    setTimeout(function () { repairStrayWlhButtons(); syncAllHearts(); }, delay);
  });

  var observer = new MutationObserver(function (mutations) {
    var needsSync = false;
    var needsInit = false;

    mutations.forEach(function (mutation) {
      if (mutation.target.matches && mutation.target.matches('[class*="wlh-btn-"]')) {
        needsSync = true;
      }
      if (mutation.target.closest && mutation.target.closest('[class*="wlh-btn-"]')) {
        needsSync = true;
      }

      if (mutation.addedNodes && mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (
            node.matches && node.matches('.product-context-trigger') ||
            (node.querySelector && node.querySelector('.product-context-trigger'))
          ) {
            needsInit = true;
          }
        });
      }
    });

    if (needsSync) {
      if (window.syncTimeout) clearTimeout(window.syncTimeout);
      window.syncTimeout = setTimeout(function () {
        repairStrayWlhButtons();
        syncAllHearts();
      }, CONFIG.OBSERVER_DEBOUNCE_MS);
    }

    if (needsInit) {
      if (window.initTimeout) clearTimeout(window.initTimeout);
      window.initTimeout = setTimeout(function () {
        initWishlistButtons();
      }, CONFIG.OBSERVER_DEBOUNCE_MS);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label', 'class', 'title', 'fill'] });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      setTimeout(syncAllHearts, CONFIG.REVISIT_SYNC_MS);
    }
  });

  window.addEventListener('load', function () {
    initWishlistButtons();
    setTimeout(function () {
      repairStrayWlhButtons();
      syncAllHearts();
    }, CONFIG.REVISIT_SYNC_MS);
  });

  // --------------------------------------------------------------------------
  // Cross-page wishlist revision — the proper fix for stale hearts on Back/Forward
  // On Back, the browser restores the page from a frozen snapshot (bfcache).
  // The SDK's in-memory state and the hidden buttons in that snapshot are stale,
  // and repairing them from the inside (re-init) is not reliable — the SDK's state
  // is only fresh after a reload. So: on every user-initiated wishlist change a
  // shared revision (localStorage) is bumped. Each page remembers its load-time
  // revision; when it comes back from bfcache / another tab and the revision has
  // changed (i.e. the wishlist changed elsewhere), ONLY THEN is the page refreshed.
  // If nothing changed, bfcache's instant Back speed is left untouched.
  // --------------------------------------------------------------------------
  var WL_REV_KEY = 'lg_wishlist_rev';

  function readWlRev() {
    try { return localStorage.getItem(WL_REV_KEY) || '0'; } catch (e) { return '0'; }
  }

  function bumpWlRev() {
    try { localStorage.setItem(WL_REV_KEY, String(Date.now())); } catch (e) {}
    // This page made the change itself, so advance its own revision as well.
    pageWlRev = readWlRev();
  }

  var pageWlRev = readWlRev();
  var wlReloading = false;

  function reloadIfWishlistChanged() {
    if (wlReloading || readWlRev() === pageWlRev) return;
    wlReloading = true;
    location.reload();
  }

  document.addEventListener('wlh:heart-state-changed', function (e) {
    if (e.detail && e.detail.userInitiated) bumpWlRev();
  });
  document.addEventListener('wlh:count-refresh', bumpWlRev);

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) reloadIfWishlistChanged();
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) reloadIfWishlistChanged();
  });

  // --------------------------------------------------------------------------
  // Header icon count-bubble (.wishlist-hero-items-count) sync
  // The SDK normally manages this badge itself, but it never updates after an
  // add/remove (not even after a reload) — so we keep our own defensive sync
  // that always derives the right number from GetWishListItemsCount(), whether
  // or not the SDK's own internal refresh runs.
  // --------------------------------------------------------------------------
  function syncHeaderBadge() {
    if (!window.WishListHero_SDK || typeof window.WishListHero_SDK.GetWishListItemsCount !== 'function') return;

    var icon = document.querySelector('.wishlist-hero-header-icon');
    if (!icon) return;

    try {
      Promise.resolve(window.WishListHero_SDK.GetWishListItemsCount())
        .then(function (count) {
          count = Number(count) || 0;
          var badge = icon.querySelector('.wishlist-hero-items-count');
          if (count > 0) {
            if (!badge) {
              badge = document.createElement('span');
              badge.className = 'wishlist-hero-items-count';
              icon.appendChild(badge);
            }
            badge.textContent = count;
          } else if (badge) {
            badge.remove();
          }
        })
        .catch(function () {});
    } catch (e) {}
  }

  CONFIG.SYNC_DELAYS_MS.forEach(function (delay) {
    setTimeout(syncHeaderBadge, delay);
  });
  window.addEventListener('load', function () {
    setTimeout(syncHeaderBadge, CONFIG.REVISIT_SYNC_MS);
  });
  // Instead of calling syncHeaderBadge() just ONCE per event, this schedules
  // several staggered re-checks — so if the SDK's count does not update
  // immediately (which happens intermittently), the later checks catch it.
  // Each check is independent/idempotent, so running again does no harm.
  function scheduleHeaderBadgeSync() {
    CONFIG.BADGE_RETRY_DELAYS_MS.forEach(function (delay) {
      setTimeout(syncHeaderBadge, delay);
    });
  }

  document.addEventListener('wlh:heart-state-changed', scheduleHeaderBadgeSync);

  // A new, additive event — fired after a path like "Remove All"
  // (assets/wishlist-custom.js), which calls the SDK directly (it does not go
  // through any individual heart click), to manually refresh the whole wishlist
  // UI (header badge + all visible hearts).
  document.addEventListener('wlh:count-refresh', function () {
    setTimeout(function () {
      repairStrayWlhButtons();
      syncAllHearts();
    }, CONFIG.TILE_TOGGLE_CONFIRM_MS);
    scheduleHeaderBadgeSync();
  });

  // --------------------------------------------------------------------------
  // The "View wishlist" link of the "Product added to wishlist" popup — this is
  // Wishlist Hero's own React/MUI snackbar (rendered entirely from their JS
  // bundle), so there is no option to customize it in their app admin. Its markup
  // has no link href at all — navigation happens through their own React click
  // handler (which is sometimes attached late and sometimes never) — hence the
  // "sometimes works, sometimes doesn't" behavior, and even when it works it
  // takes the user to their old app page.
  //
  // Fix: we intercept on the document in the CAPTURE phase — so their own React
  // handler (attached somewhere higher in the DOM tree, in the bubble phase) never
  // receives the event; we stop it first and do our own navigation. The selector
  // intentionally does not depend on their auto-generated CSS classes like "jss4"
  // (which can change on every app rebuild) — only on their stable
  // "wishlist-hero-*" id/class prefix and the visible text of the link.
  // --------------------------------------------------------------------------
  var WISHLIST_PAGE_URL = '/pages/my-wishlist'; // same as in wishlisthero-header-icon.liquid

  document.addEventListener(
    'click',
    function (e) {
      var scope = e.target.closest('[id*="wishlist-hero"], [class*="wishlist-hero"]');
      if (!scope) return;

      var link = e.target.closest('a, button');
      if (!link) return;

      var text = (link.textContent || '').trim().toLowerCase();
      if (text !== 'view wishlist') return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      window.location.href = WISHLIST_PAGE_URL;
    },
    true
  );
});
